import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { AlertTriangle, Building2, Calculator, CheckCircle, Save } from 'lucide-react';
import type { FinancialAidStaffDetailContext } from '../ApplicantDetail';
import { asArray, asRecord } from '../../utils/domainData';
import {
  readParentCollectedFinancialAidSnapshot,
  readRawAmount,
  readRawBoolean,
  readSnapshotRecord,
} from './staffFinancialAidRawSnapshot';
import { resolveFinancialAidDisplayYears } from '../../../../financial-aid/financialAidAcademicYear';

const readText = (value: unknown, fallback = ''): string => {
  const next = String(value ?? '').trim();
  return next || fallback;
};

const asNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasOwnRecordKey = (value: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const formatCurrency = (val: number) => {
  const isNegative = val < 0;
  const formatted = Math.abs(val).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return isNegative ? `(${formatted})` : formatted;
};

const formatSignedCurrency = (val: number) => {
  const isNegative = val < 0;
  const formatted = Math.abs(val).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return isNegative ? `-${formatted}` : formatted;
};

const incomeBreakdownTitle = (actualYear: number | null): string =>
  actualYear ? `Reported Income Breakdown (${actualYear})` : 'Reported Income Breakdown';

const editableWorksheetInputValue = (value: number): string =>
  value === 0 ? '' : String(value);

const readEditableWorksheetInputValue = (value: string): number => {
  const trimmed = value.trim();
  if (!trimmed) {
    return 0;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : 0;
};

export type IncomeCalculationView = {
  reportedBreakdown: {
    father: { gross: number; parsonage: number; taxes: number };
    mother: { gross: number; parsonage: number; taxes: number };
  };
  unemploymentBenefits: {
    father: number;
    mother: number;
    show: boolean;
  };
  worksheet: {
    agiFromTaxReturn: number;
    otherNonTaxable: number;
    housingAllowance: number;
    fairRentalValue: number;
  };
  methodology: string;
};

type ParentIncomeKey = 'father' | 'mother';
type WorksheetAmountKey = 'agi' | 'other' | 'housing' | 'parsonage';
type IncomeTabContext = Omit<
  FinancialAidStaffDetailContext,
  'isCorrectionEditing' | 'draftSections' | 'updateCorrectionSection'
> & Partial<Pick<FinancialAidStaffDetailContext, 'isCorrectionEditing' | 'draftSections' | 'updateCorrectionSection'>>;

const toParentKey = (parentIndex: unknown): ParentIncomeKey =>
  asNumber(parentIndex) === 2 ? 'mother' : 'father';

const inferParentKey = (entry: Record<string, unknown>): ParentIncomeKey => {
  if (entry.parentIndex !== undefined && entry.parentIndex !== null) {
    return toParentKey(entry.parentIndex);
  }

  const parentHint = [
    entry.parent,
    entry.parentName,
    entry.parentLabel,
    entry.description,
  ].map((value) => readText(value).toLowerCase()).join(' ');

  if (/\b(mother|mom|parent\s*2|parent2)\b/.test(parentHint)) {
    return 'mother';
  }

  return 'father';
};

const sumTaxFields = (entry: Record<string, unknown>): number => (
  asNumber(entry.taxesAnnual)
  + asNumber(entry.ficaAnnual)
  + asNumber(entry.stateTaxesAnnual)
  + asNumber(entry.federalTax)
  + asNumber(entry.stateTax)
  + asNumber(entry.ficaTax)
);

const approximatelyEqualCurrency = (first: number, second: number): boolean =>
  Math.abs(first - second) < 0.01;

const isDuplicatePrimaryIncomeEntry = (
  annualAmount: number,
  parentKey: ParentIncomeKey,
  employmentSalaries: Record<ParentIncomeKey, number[]>
): boolean => {
  if (annualAmount <= 0) {
    return false;
  }

  return employmentSalaries[parentKey].some((salary) => approximatelyEqualCurrency(salary, annualAmount));
};

const isNonGrossSupportIncomeEntry = (
  incomeType: string,
  entry: Record<string, unknown>,
): boolean => {
  const label = [
    incomeType,
    readText(entry.description),
    readText(entry.category),
  ].join(' ').toLowerCase();
  return /\b(gift|gifts|funding|support)\b/.test(label);
};

const applyParentCollectedIncome = (
  target: IncomeCalculationView['reportedBreakdown'][ParentIncomeKey],
  rawIncome: Record<string, unknown>,
): void => {
  const gross = readRawAmount(rawIncome.gross2024);
  const parsonage = readRawAmount(rawIncome.parsonage);
  const taxes = readRawAmount(rawIncome.fedTax)
    + readRawAmount(rawIncome.stateTax)
    + readRawAmount(rawIncome.ficaTax);

  if (gross > 0 && target.gross <= 0) target.gross = gross;
  if (parsonage > 0 && target.parsonage <= 0) target.parsonage = parsonage;
  if (taxes > 0 && target.taxes <= 0) target.taxes = taxes;
};

const readParentSectionIncomeKey = (
  parent: Record<string, unknown>,
  fallback: ParentIncomeKey,
): ParentIncomeKey => {
  const normalized = readText(parent.relationship).toLowerCase().replace(/[\s_-]+/g, '');
  if (['mother', 'mom', 'parent2', 'guardian2', 'stepmother', 'stepmom'].includes(normalized)) {
    return 'mother';
  }
  if (['father', 'dad', 'parent1', 'guardian1', 'stepfather', 'stepdad'].includes(normalized)) {
    return 'father';
  }
  return fallback;
};

const resolveCanonicalParentUnemployment = (
  parentSections: Array<{ parent: Record<string, unknown>; fallback: ParentIncomeKey }>,
  parentKey: ParentIncomeKey,
): { weeklyAmount: number; isUnemployed: boolean } => {
  const match = parentSections.find(({ parent, fallback }) =>
    readParentSectionIncomeKey(parent, fallback) === parentKey
  )?.parent;
  if (!match) {
    return { weeklyAmount: 0, isUnemployed: false };
  }
  return {
    weeklyAmount: readRawAmount(match.unemploymentWeeklyAmount),
    isUnemployed: readRawBoolean(match.isUnemployed),
  };
};

export const buildIncomeCalculation = (review: FinancialAidStaffDetailContext['review']): IncomeCalculationView => {
  const reviewRecord = asRecord(review as unknown);
  const application = asRecord(reviewRecord.application);
  const sections = asRecord(application.sections);
  const metrics = asRecord(application.metrics);

  const employment = asRecord(sections.employment);
  const employments = asArray<Record<string, unknown>>(employment.employments);
  const income = asRecord(sections.income);
  const incomeEntries = asArray<Record<string, unknown>>(income.entries);

  const reportedBreakdown = {
    father: { gross: 0, parsonage: 0, taxes: 0 },
    mother: { gross: 0, parsonage: 0, taxes: 0 },
  };
  const employmentSalaries: Record<ParentIncomeKey, number[]> = {
    father: [],
    mother: [],
  };

  for (const entry of employments) {
    const parentKey = inferParentKey(entry);
    const annualSalary = asNumber(entry.annualSalary);
    reportedBreakdown[parentKey].gross += annualSalary;
    if (annualSalary > 0) {
      employmentSalaries[parentKey].push(annualSalary);
    }
    reportedBreakdown[parentKey].parsonage += asNumber(entry.parsonageAmount) + asNumber(entry.housingAllowance);
    reportedBreakdown[parentKey].taxes += sumTaxFields(entry);
  }

  let otherNonTaxable = 0;
  for (const entry of incomeEntries) {
    const parentKey = inferParentKey(entry);
    const annualAmount = asNumber(entry.annualAmount) || asNumber(entry.amount);
    const incomeType = readText(entry.type).toLowerCase();

    if (/tax/i.test(incomeType)) {
      reportedBreakdown[parentKey].taxes += annualAmount;
      continue;
    }
    if (/parsonage|housing/i.test(incomeType)) {
      reportedBreakdown[parentKey].parsonage += annualAmount;
      continue;
    }

    const isPrimaryIncome = /salary|wage|employment/i.test(incomeType);
    if (isPrimaryIncome && isDuplicatePrimaryIncomeEntry(annualAmount, parentKey, employmentSalaries)) {
      continue;
    }

    if (!isPrimaryIncome) {
      if (isNonGrossSupportIncomeEntry(incomeType, entry)) {
        otherNonTaxable += annualAmount;
        continue;
      }
    }

    reportedBreakdown[parentKey].gross += annualAmount;
  }

  const metricsFatherGross = asNumber(metrics.fatherIncome) || asNumber(metrics.parent1Income);
  const metricsMotherGross = asNumber(metrics.motherIncome) || asNumber(metrics.parent2Income);
  if (metricsFatherGross > 0 && reportedBreakdown.father.gross === 0) {
    reportedBreakdown.father.gross = metricsFatherGross;
  }
  if (metricsMotherGross > 0 && reportedBreakdown.mother.gross === 0) {
    reportedBreakdown.mother.gross = metricsMotherGross;
  }

  const rawSnapshot = readParentCollectedFinancialAidSnapshot(review);
  applyParentCollectedIncome(
    reportedBreakdown.father,
    readSnapshotRecord(rawSnapshot, 'incomeFather')
  );
  applyParentCollectedIncome(
    reportedBreakdown.mother,
    readSnapshotRecord(rawSnapshot, 'incomeMother')
  );

  const rawFatherParent = readSnapshotRecord(rawSnapshot, 'father');
  const rawMotherParent = readSnapshotRecord(rawSnapshot, 'mother');
  const parentSections = [
    { parent: asRecord(sections.parent1), fallback: 'father' as const },
    { parent: asRecord(sections.parent2), fallback: 'mother' as const },
  ];
  const canonicalFatherUnemployment = resolveCanonicalParentUnemployment(parentSections, 'father');
  const canonicalMotherUnemployment = resolveCanonicalParentUnemployment(parentSections, 'mother');
  const fatherUnemploymentWeekly = readRawAmount(rawFatherParent.unemploymentWeeklyAmount)
    || canonicalFatherUnemployment.weeklyAmount;
  const motherUnemploymentWeekly = readRawAmount(rawMotherParent.unemploymentWeeklyAmount)
    || canonicalMotherUnemployment.weeklyAmount;
  const showUnemploymentBenefitsRow =
    readRawBoolean(rawFatherParent.isUnemployed)
    || readRawBoolean(rawMotherParent.isUnemployed)
    || canonicalFatherUnemployment.isUnemployed
    || canonicalMotherUnemployment.isUnemployed
    || fatherUnemploymentWeekly > 0
    || motherUnemploymentWeekly > 0;

  const agiFromTaxReturn = asNumber(metrics.agiFromTaxReturn)
    || asNumber(metrics.adjustedGrossIncome)
    || asNumber(metrics.totalAnnualIncome)
    || asNumber(income.totalAnnualIncome);

  const housingAllowance = asNumber(metrics.housingAllowance)
    || Math.abs(reportedBreakdown.father.parsonage + reportedBreakdown.mother.parsonage);

  const fairRentalValue = asNumber(metrics.fairRentalValue)
    || asNumber(metrics.parsonageFairRentalValue);

  return {
    reportedBreakdown,
    unemploymentBenefits: {
      father: fatherUnemploymentWeekly,
      mother: motherUnemploymentWeekly,
      show: showUnemploymentBenefitsRow,
    },
    worksheet: {
      agiFromTaxReturn,
      otherNonTaxable: Math.abs(otherNonTaxable),
      housingAllowance: Math.abs(housingAllowance),
      fairRentalValue: Math.abs(fairRentalValue),
    },
    methodology: readText(metrics.calculationMethod, 'FAFSA-aligned (clergy housing excluded)'),
  };
};

export const IncomeTab: React.FC = () => {
  const context = useOutletContext<IncomeTabContext>();
  const {
    review,
    layoutMode,
  } = context;
  const correctionModeActive = context.isCorrectionEditing ?? false;
  const isCorrectionEditing = false;
  const draftSections = context.draftSections ?? {};
  const updateCorrectionSection = (sectionKey: string, patch: unknown) => {
    if (typeof context.updateCorrectionSection === 'function') {
      context.updateCorrectionSection(sectionKey, patch);
      return;
    }
    if (correctionModeActive) {
      throw new Error('Financial aid correction context is unavailable.');
    }
  };
  const isCompactLayout = layoutMode === 'compact';
  const reviewRecord = asRecord(review as unknown);
  const applicationRecord = asRecord(reviewRecord.application);
  const displayYears = resolveFinancialAidDisplayYears(applicationRecord.academicYear);

  const incomeCalculation = useMemo(() => buildIncomeCalculation(review), [review]);

  const [isEditing, setIsEditing] = useState(false);
  const [worksheet, setWorksheet] = useState({
    agi: Math.abs(incomeCalculation.worksheet.agiFromTaxReturn),
    other: Math.abs(incomeCalculation.worksheet.otherNonTaxable),
    housing: Math.abs(incomeCalculation.worksheet.housingAllowance),
    parsonage: Math.abs(incomeCalculation.worksheet.fairRentalValue),
    override: false,
  });

  useEffect(() => {
    setWorksheet({
      agi: Math.abs(incomeCalculation.worksheet.agiFromTaxReturn),
      other: Math.abs(incomeCalculation.worksheet.otherNonTaxable),
      housing: Math.abs(incomeCalculation.worksheet.housingAllowance),
      parsonage: Math.abs(incomeCalculation.worksheet.fairRentalValue),
      override: false,
    });
  }, [incomeCalculation]);

  if (!review) {
    return <p className="text-sm text-[#6B7280]">Application data is syncing.</p>;
  }

  const calculateAdjusted = () => worksheet.agi + worksheet.other - worksheet.housing - worksheet.parsonage;
  const application = asRecord(reviewRecord.application);
  const sections = asRecord(application.sections);
  const incomeSection = asRecord(sections.income);
  const draftIncome = asRecord(draftSections.income);
  const hasDraftIncomeTotal = hasOwnRecordKey(draftIncome, 'totalAnnualIncome');

  const totalReportedGross = incomeCalculation.reportedBreakdown.father.gross
    + incomeCalculation.reportedBreakdown.father.parsonage
    + incomeCalculation.reportedBreakdown.mother.gross
    + incomeCalculation.reportedBreakdown.mother.parsonage;
  const correctedTotalAnnualIncome = hasDraftIncomeTotal
    ? asNumber(draftIncome.totalAnnualIncome)
    : asNumber(incomeSection.totalAnnualIncome) || totalReportedGross;
  const updateTotalAnnualIncome = (rawValue: string) => {
    const trimmedValue = rawValue.trim();
    if (!trimmedValue) {
      return;
    }
    const value = Number(trimmedValue);
    if (!Number.isFinite(value) || value < 0) {
      return;
    }
    updateCorrectionSection('income', {
      totalAnnualIncome: value,
    });
  };

  const showHousingWarning = worksheet.agi > 0 && (worksheet.housing / worksheet.agi) > 0.5;

  const renderWorksheetAmountInput = (field: WorksheetAmountKey, label: string) => {
    const inputId = `staff-calculation-${field}`;

    return (
      <div>
        <label htmlFor={inputId} className="text-xs font-bold text-[#4B5563] mb-2 block">{label}</label>
        <input
          id={inputId}
          type="number"
          inputMode="decimal"
          placeholder="0"
          value={editableWorksheetInputValue(worksheet[field])}
          onChange={(event) => {
            setWorksheet({
              ...worksheet,
              [field]: readEditableWorksheetInputValue(event.target.value),
            });
          }}
          className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm font-bold focus:ring-primary focus:border-primary"
        />
      </div>
    );
  };

  const IncomeRow = ({
    label,
    father,
    mother,
    isNegative = false,
  }: {
    label: string;
    father: number;
    mother: number;
    isNegative?: boolean;
  }) => (
    <div className="flex justify-between items-center text-sm py-1">
      <span className="text-[#6B7280]">{label}:</span>
      <div className="flex w-1/2 justify-between">
        <span className={`font-semibold ${isNegative ? 'text-strawberry' : 'text-[#404F4D]'}`}>
          {formatCurrency(father)}
        </span>
        <span className={`font-semibold ${isNegative ? 'text-strawberry' : 'text-[#404F4D]'}`}>
          {formatCurrency(mother)}
        </span>
      </div>
    </div>
  );

  return (
    <div data-testid="financial-aid-income-calculation-tab" className="space-y-8 max-w-5xl">
      <div className="bg-white rounded-xl border overflow-hidden shadow-card" style={{ borderColor: '#E5E7EB' }}>
        <div className="flex items-center gap-3 p-5 bg-[#FAFBF8] border-b" style={{ borderColor: '#E5E7EB' }}>
          <div className="p-2 rounded-lg bg-[#E2F1F1] text-jungle">
            <Building2 size={20} />
          </div>
          <h2 className="text-lg font-bold text-[#111827]">{incomeBreakdownTitle(displayYears.actualYear)}</h2>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="border-b pb-2" style={{ borderColor: '#E5E7EB' }}>
              <h3 className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-widest">FATHER&apos;S INCOME</h3>
            </div>
            <div className="border-b pb-2" style={{ borderColor: '#E5E7EB' }}>
              <h3 className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-widest">MOTHER&apos;S INCOME</h3>
            </div>
          </div>

          <div className="space-y-2">
            <IncomeRow
              label="Gross Income"
              father={incomeCalculation.reportedBreakdown.father.gross}
              mother={incomeCalculation.reportedBreakdown.mother.gross}
            />
            <IncomeRow
              label="Parsonage"
              father={incomeCalculation.reportedBreakdown.father.parsonage}
              mother={incomeCalculation.reportedBreakdown.mother.parsonage}
            />
            <IncomeRow
              label="Taxes (Fed/State/FICA)"
              father={-Math.abs(incomeCalculation.reportedBreakdown.father.taxes)}
              mother={-Math.abs(incomeCalculation.reportedBreakdown.mother.taxes)}
              isNegative
            />
            {incomeCalculation.unemploymentBenefits.show ? (
              <IncomeRow
                label="Unemployment Benefits (Weekly)"
                father={incomeCalculation.unemploymentBenefits.father}
                mother={incomeCalculation.unemploymentBenefits.mother}
              />
            ) : null}
          </div>
        </div>

        <div className={`px-6 py-4 bg-[#FAFBF8] border-t flex ${isCompactLayout ? 'flex-col items-start gap-3' : 'justify-between items-center'}`} style={{ borderColor: '#E5E7EB' }}>
          <span className="text-sm font-bold text-[#404F4D]">Total Reported Gross Income</span>
          {isCorrectionEditing ? (
            <label className="flex w-full max-w-xs flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Corrected Annual Income</span>
              <input
                type="number"
                min={0}
                value={correctedTotalAnnualIncome}
                onChange={(event) => updateTotalAnnualIncome(event.currentTarget.value)}
                className="w-full rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 text-sm font-bold text-[#111827] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
          ) : (
            <span className="text-2xl font-black text-[#111827]">
              {formatCurrency(totalReportedGross)}
            </span>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden shadow-card" style={{ borderColor: '#E5E7EB' }}>
        <div className="flex items-center justify-between p-5 bg-[#FAFBF8] border-b" style={{ borderColor: '#E5E7EB' }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white border shadow-sm text-[#404F4D]">
              <Calculator size={20} />
            </div>
            <h2 className="text-lg font-bold text-[#111827]">Staff Calculation Worksheet</h2>
          </div>
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="text-xs font-bold text-[#404F4D] border rounded-lg px-4 py-2 hover:bg-white transition-all shadow-sm"
              style={{ borderColor: '#D1D5DB' }}
            >
              Edit Calculation
            </button>
          )}
        </div>

        <div className="p-8">
          {!isEditing ? (
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-[#4B5563]">AGI from Tax Return:</span>
                  <span className="text-sm font-bold text-[#111827]">{formatCurrency(worksheet.agi)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-[#4B5563]">+ Other Non-taxable Income:</span>
                  <span className="text-sm font-bold text-[#111827]">{formatCurrency(worksheet.other)}</span>
                </div>
                <div className="flex justify-between items-center text-strawberry">
                  <span className="text-sm font-medium">- Housing Allowance:</span>
                  <span className="text-sm font-bold">{formatSignedCurrency(-worksheet.housing)}</span>
                </div>
                <div className="flex justify-between items-center text-strawberry">
                  <span className="text-sm font-medium">- Fair Rental Value (Parsonage):</span>
                  <span className="text-sm font-bold">{formatSignedCurrency(-worksheet.parsonage)}</span>
                </div>
              </div>

              <div className="border-t pt-4" style={{ borderColor: '#E5E7EB' }}>
                <div className="flex justify-between items-center bg-[#FAFBF8] p-4 rounded-xl border border-dashed" style={{ borderColor: '#D1D5DB' }}>
                  <span className="text-sm font-bold text-[#4B5563] uppercase tracking-wide">= Adjusted Income for Aid:</span>
                  <span className="text-2xl font-black text-jungle">
                    {formatCurrency(calculateAdjusted())}
                  </span>
                </div>
              </div>

              <div className="pt-4 flex items-center gap-3">
                <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-widest">Methodology:</span>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#ECFDF5] text-jungle border border-[#D1FAE5]">
                  <CheckCircle size={14} />
                  {incomeCalculation.methodology}
                </div>
              </div>

              {showHousingWarning && (
                <div className="mt-6 flex items-start gap-3 p-4 bg-[#FEF3C7] border border-[#FDE68A] rounded-xl text-[#92400E]">
                  <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                  <p className="text-sm font-bold tracking-tight">
                    Housing allowance unusually high (&gt; 50% of AGI) - verify accuracy
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <div className={`grid ${isCompactLayout ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                {renderWorksheetAmountInput('agi', 'AGI from Tax Return')}
                {renderWorksheetAmountInput('other', 'Other Non-taxable Income')}
                {renderWorksheetAmountInput('housing', 'Housing Allowance (Annual)')}
                {renderWorksheetAmountInput('parsonage', 'Fair Rental Value (Annual)')}
              </div>

              <div className="p-5 bg-[#FFF7ED] border border-[#FFEDD5] rounded-xl flex items-center gap-3">
                <input
                  type="checkbox"
                  id="override"
                  checked={worksheet.override}
                  onChange={(event) => setWorksheet({ ...worksheet, override: event.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                />
                <label htmlFor="override" className="text-sm font-bold text-[#9A3412]">
                  Include housing benefit in income calculation (Override)
                </label>
              </div>

              <div className="pt-8 border-t flex flex-col items-center justify-center gap-6" style={{ borderColor: '#E5E7EB' }}>
                <div className="text-center">
                  <p className="text-xs font-bold text-[#9CA3AF] uppercase tracking-widest mb-1">Preview Adjusted Income</p>
                  <p className="text-3xl font-black text-jungle">{formatCurrency(calculateAdjusted())}</p>
                </div>
                <div className="flex gap-4 w-full justify-center">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-8 py-3 bg-[#F3F4F6] text-[#4B5563] font-bold rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-8 py-3 bg-[#1C3B3E] text-white font-bold rounded-xl hover:bg-[#234b4f] transition-all flex items-center gap-2 shadow-lg shadow-black/5"
                  >
                    <Save size={18} />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
